import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeAnalyticsPost.graphql';
import { Query, QueryyoutubeAnalyticsPostArgs } from '../../__generated__/globalTypes';

export const useYoutubeAnalyticsPostQuery = (options?: QueryHookOptions<Query, QueryyoutubeAnalyticsPostArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeAnalyticsPostArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeAnalyticsPostLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeAnalyticsPostArgs>
): QueryResult<Query, QueryyoutubeAnalyticsPostArgs> & {
  getYoutubeAnalyticsPost: LazyQueryExecFunction<Query, QueryyoutubeAnalyticsPostArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeAnalyticsPost, result] = useLazyQuery<Query, QueryyoutubeAnalyticsPostArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeAnalyticsPost, ...result };
};

export const useYoutubeAnalyticsPostPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeAnalyticsPost = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeAnalyticsPostArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeAnalyticsPostArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeAnalyticsPost };
};
// Suspense Query Hooks
export const useYoutubeAnalyticsPostSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeAnalyticsPostArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeAnalyticsPostArgs>(QUERY, {
    ...options
  });

  return result;
};
